import React, { useState, useEffect, useContext } from "react";
import Select from "../../component/material/select/Select";
import StackChart from "./charts/StackChart";
import PieChart from "./charts/PieChart";
import * as executiveApi from "../../service/executiveApi";
import * as publicApi from "../../service/publicApi";
import { RedirectCode400 } from "../RedirectCode400";
import { useNavigate } from "react-router-dom";
import { CurrentUserContext } from "../../App";
import styles from "./styleProvider.module.css";
import Input from "../material/input/Input";
import HashLoader from "react-spinners/HashLoader";
import Breadcrumb from "../public/breacrumb/breadcrumb";
import { FaDownload } from "react-icons/fa";

function GeneResult() {
  const { setIsMessage } = useContext(CurrentUserContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [healthAreaList, setHealthAreaList] = useState([]);
  const [provinceList, setProvinceList] = useState([]);
  const [districtList, setDistrictList] = useState([]);
  const [labListSelect, setLabListSelect] = useState([]);

  const [healthAreaId, setHealthAreaId] = useState("");
  const [provinceId, setProvinceId] = useState("");
  const [districtId, setDistrictId] = useState("");
  const [labId, setLabId] = useState("");
  const [approveDate, setApproveDate] = useState("");

  const [allergyGeneList, setAllergyGeneList] = useState({});
  const [allergyGeneChartData, setAllergyGeneChartData] = useState({ values: [0, 0] });
  const [degradingGeneChartData, setDegradingGeneChartData] = useState({ values: [0, 0, 0] });

  useEffect(() => {
    fetchData({});
    loadHealthAreaInitial();
  }, []);

  const loadHealthAreaInitial = async () => {
    let res = await executiveApi.getAnalytic();
    if (res?.data?.code === 200) {
      setHealthAreaList(
        res.data.analytic_list
          .sort((a, b) => parseFloat(a.id) - parseFloat(b.id))
          .map((item) => ({ key: item.id, value: item.name }))
      );
    }
  };

  const fetchData = async (filters) => {
    setLoading(true);
    let res = await executiveApi.getAnalytic(filters);
    setLoading(false);

    if (res?.data?.code === 200) {
      processAnalyticData(res.data.analytic_list);
    } else {
      handleApiError(res);
    }
  };

  const processAnalyticData = (list) => {
    if (!list || list.length === 0) {
      resetCharts();
      return;
    }

    const summary = list.reduce((acc, curr) => {
      Object.keys(curr.drug_allergy_gene).forEach((gene) => {
        if (!acc.allergy[gene]) acc.allergy[gene] = { positive: 0, negative: 0 };
        acc.allergy[gene].positive += curr.drug_allergy_gene[gene].positive || 0;
        acc.allergy[gene].negative += curr.drug_allergy_gene[gene].negative || 0;
      });

      acc.degrading.slow += curr.drug_degrading_gene.slow_acetylator || 0;
      acc.degrading.inter += curr.drug_degrading_gene.intermediate_acetylator || 0;
      acc.degrading.rapid += curr.drug_degrading_gene.rapid_acetylator || 0;

      return acc;
    }, { allergy: {}, degrading: { slow: 0, inter: 0, rapid: 0 } });

    setAllergyGeneList(summary.allergy);

    const totalPos = (summary.allergy.hla_b_15_02?.positive || 0) +
                     (summary.allergy.hla_b_58_01?.positive || 0) +
                     (summary.allergy.hla_b_57_01?.positive || 0);
    const totalNeg = (summary.allergy.hla_b_15_02?.negative || 0) +
                     (summary.allergy.hla_b_58_01?.negative || 0) +
                     (summary.allergy.hla_b_57_01?.negative || 0);

    setAllergyGeneChartData({ values: [totalPos, totalNeg] });
    setDegradingGeneChartData({ 
      values: [summary.degrading.slow, summary.degrading.inter, summary.degrading.rapid] 
    });
  };

  const resetCharts = () => {
    setAllergyGeneList({});
    setAllergyGeneChartData({ values: [0, 0] });
    setDegradingGeneChartData({ values: [0, 0, 0] });
  };

  const handleAreaChange = async (val) => {
    setHealthAreaId(val);
    setProvinceId(""); setDistrictId(""); setLabId(""); setApproveDate("");
    setProvinceList([]); setDistrictList([]); setLabListSelect([]);

    if (val === "") {
      fetchData();
      return;
    } else {
      let res = await publicApi.getProvinceList(val);
      if (res?.data?.code === 200) {
        const list = res.data.province_list;
        setProvinceList(list.map(item => ({ key: item.province_id, value: item.province_name })));
      }
    }

    setLoading(true);
    let res = await executiveApi.getAnalytic({ health_area: val });
    setLoading(false);
    if (res?.data?.code === 200) {
      const list = res.data.analytic_list;
      processAnalyticData(list);
    } else {
      handleApiError(res);
    }
  };

  const handleProvinceChange = async (val) => {
    setProvinceId(val);
    setDistrictId(""); setLabId(""); setApproveDate("");
    setDistrictList([]); setLabListSelect([]);

    if (val === "") {
      fetchData();
      return;
    } else {
      let res = await publicApi.getDistrictList(val);
      if (res?.data?.code === 200) {
        const list = res.data.district_list;
        setDistrictList(list.map(item => ({ key: item.district_id, value: item.district_name })));
      }
    }

    setLoading(true);
    let res = await executiveApi.getAnalytic({ province_id: val });
    setLoading(false);
    if (res?.data?.code === 200) {
      const list = res.data.analytic_list;
      processAnalyticData(list);
    } else {
      handleApiError(res);
    }
  };

  const handleDistrictChange = async (val) => {
    setDistrictId(val);
    setLabId(""); setApproveDate("");
    setLabListSelect([]);

    if (val === "") {
      fetchData();
      return;
    } else {
      let res = await publicApi.getLaboratoryList(val);
      if (res?.data?.code === 200) {
        const list = res.data.laboratory_list;
        setLabListSelect(list.map(item => ({ key: item.laboratory_id, value: item.laboratory_name })));
      }
    }

    setLoading(true);
    let res = await executiveApi.getAnalytic({ district_id: val });
    setLoading(false);
    if (res?.data?.code === 200) {
      const list = res.data.analytic_list;
      processAnalyticData(list);
    } else {
      handleApiError(res);
    }
  };

  const handleLabChange = (val) => {
    setLabId(val);
    setApproveDate("");
    fetchData({ laboratory_id: val, district_id: districtId });
  };

  const handleDateChange = (val) => {
    setApproveDate(val);
    fetchData({ laboratory_id: labId, approve_date: val });
  };

  const clickExportFile = (type) => {
    const filters = approveDate ? { approve_date: approveDate, laboratory_id: labId }
                  : labId ? { laboratory_id: labId }
                  : districtId ? { district_id: districtId }
                  : provinceId ? { province_id: provinceId }
                  : { health_area: healthAreaId };

    if (type === "number") exportAnalytic(filters);
    else exportAnalyticData(filters);
  };

  const exportAnalytic = async (data) => {
    setLoading(true);
    let res = await executiveApi.exportAnalytic(data);
    setLoading(false);
    if (res?.data?.code === 200) downloadURI(res.data.file, "summary_report.xlsx");
    else handleApiError(res);
  };

  const exportAnalyticData = async (data) => {
    setLoading(true);
    let res = await executiveApi.exportAnalyticData(data);
    setLoading(false);
    if (res?.data?.code === 200) downloadURI(res.data.file, "individual_report.xlsx");
    else handleApiError(res);
  };

  const exportAllLabDataFile = async () => {
    const filters = districtId ? { district_id: districtId }
                  : provinceId ? { province_id: provinceId }
                  : healthAreaId ? { health_area: healthAreaId }
                  : {};
    setLoading(true);
    let res = await executiveApi.exportAllLabData(filters);
    setLoading(false);
    if (res?.data?.code === 200) downloadURI(res.data.file, "all_lab_result.xlsx");
    else handleApiError(res);
  };

  const downloadURI = (uri, name) => {
    const link = document.createElement("a");
    link.download = name;
    link.href = uri;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleApiError = (res) => {
    if (res?.data?.code === 403) navigate(RedirectCode400());
    else setIsMessage({ type: "warning", data: res?.data?.message || "เกิดข้อผิดพลาด" });
  };

  const pathItems = [
    { name: "หน้าหลัก", url: "/" },
    { name: "ผลสรุป", url: "/manager/aggregateResult" },
    { name: "สรุปผลการตรวจยีนทั้งหมด", url: "" },
  ];

  return (
    <div className={styles.page}>
      {loading && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/50">
          <HashLoader loading={loading} color="#FF9768" size={60} />
        </div>
      )}

      <div className="mt-[10px]">
        <Breadcrumb pathItems={pathItems} />
      </div>

      <div className={styles.sidepage}>
        <p className="text-center text-2xl font-bold lg:mt-[20px] mt-[70px] font-['Prompt-SemiBold']">
          ผลสรุป
        </p>

        <div className={styles.menuTab}>
          <div className={styles.tab} onClick={() => navigate("/manager/aggregateResult")}>
            สรุปจำนวนการเข้าใช้งาน
          </div>
          <div className={styles.tabActive}>สรุปผลการตรวจยีนทั้งหมด</div>
          <div className={styles.tab} onClick={() => navigate("/manager/inspectionRateMap")}>
            แผนที่ห้องปฏิบัติการที่มีในระบบ
          </div>
        </div>

        <p className="text-left text-2xl font-bold my-4 font-['Prompt-SemiBold']">
          สรุปผลการตรวจยีนทั้งหมด
        </p>

        <div className={styles.select}>
          <Select
            placeholder="ทั้งหมด"
            label="เลือกเขตสุขภาพ"
            value={healthAreaId}
            list={healthAreaList}
            setValue={handleAreaChange}
            style={{ borderRadius: "5px" }}
          />
          <Select
            placeholder="เลือกจังหวัด"
            label="เลือกจังหวัด"
            value={provinceId}
            list={provinceList}
            disabled={!healthAreaId}
            setValue={handleProvinceChange}
            style={{ borderRadius: "5px" }}
          />
          <Select
            placeholder="เลือกอำเภอ"
            label="เลือกอำเภอ"
            value={districtId}
            list={districtList}
            disabled={!provinceId}
            setValue={handleDistrictChange}
            style={{ borderRadius: "5px" }}
          />
          <Select
            placeholder="เลือกห้องปฏิบัติการ"
            label="เลือกห้องปฏิบัติการ"
            value={labId}
            list={labListSelect}
            disabled={!districtId}
            setValue={handleLabChange}
            style={{ borderRadius: "5px" }}
          />
          <Input
            type="date"
            label="เลือกวันที่รับรองผล"
            value={approveDate}
            disabled={!labId}
            setValue={handleDateChange}
            style={{ borderRadius: "5px" }}
          />
        </div>

        {/* Export Buttons */}
        <div className={styles.buttonGeneResult}>
          <div className={styles.textDownloadGeneResult}>สรุปผลการตรวจ</div>
          <div onClick={() => clickExportFile("number")} className={styles.buttonDownloadGeneResult}>
            <FaDownload className="mr-2" />
            <div>ดาวน์โหลดสรุปจำนวนผลการตรวจไฟล์ excel</div>
          </div>
          <div onClick={() => clickExportFile("result")} className={styles.buttonDownloadGeneResult2}>
            <FaDownload className="mr-2" />
            <div>ดาวน์โหลดสรุปผลตรวจรายบุคคลไฟล์ excel</div>
          </div>
          {/* <div onClick={exportAllLabDataFile} className={styles.buttonDownloadGeneResult2}>
            <FaDownload className="mr-2" />
            <div>สรุปผลการตรวจทั้งหมด</div>
          </div> */}
        </div>

        {/* Charts and Data Display */}
        <div className="flex flex-col my-10 space-y-10 2xl:mx-10">
          {/* Section: Drug Allergy Genes */}
          <div className="w-full border-2 rounded-lg p-6">
            <div className={styles.align}>
              <div className={styles.Pie}>
                <div className="font-bold text-2xl mb-4 font-['Prompt-SemiBold']">การตรวจยีนแพ้ยา</div>
                <PieChart data={allergyGeneChartData} />
              </div>

              <div className="flex flex-col self-baseline w-full">
                <div className="font-bold text-2xl mb-10 font-['Prompt-SemiBold']">การตรวจยีน</div>
                <div className={styles.StackAlign}>
                  {['hla_b_15_02', 'hla_b_58_01', 'hla_b_57_01', 'hla_a_31_01', 'hla_b_13_01'].map((gene) => (
                    <div key={gene} className={styles.Stack}>
                      <div className="uppercase">
                        การตรวจยีน {gene.replace(/_/g, '* ').replace('hla', 'HLA')}
                      </div>
                      <StackChart data={allergyGeneList[gene] || { positive: 0, negative: 0 }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Section: Drug Degrading Genes */}
          <div className="w-full border-2 rounded-lg p-6">
            <div className={styles.align2}>
              <div className="flex flex-wrap w-full mb-4">
                <div className="font-bold text-2xl font-['Prompt-SemiBold'] mr-2">การตรวจยีนย่อยสลายยา:</div>
                <div className="font-bold text-2xl font-['Prompt-SemiBold']">NAT2-Diplotype</div>
              </div>
              <div className={styles.Pie}>
                <PieChart data={degradingGeneChartData} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GeneResult;